import { BCNotification, findMessage } from '@bimser/components';
import { IBaseAction } from '@bimser/core';
import { Map } from 'immutable';
import * as React from 'react';
import { all, call, delay, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import { MessageBox, panelType } from '../../..';
import { BaseState } from '../../../entities/BaseState';
import { ServiceList } from '../../../services/serviceList';
import { getMLProperty } from '../../../utilities/helpers';
import { IMessageBoxResolvePayload } from '../../ModalManager/modals/MessageBox/entities';
import {
	afterStopTourTransaction,
	loadTourAction,
	onChangeTourLoadingAction,
	onTourNextAction,
	onTourPrevAction,
	setToursAction,
	startTourAction,
	stopTourAction
} from '../actions';
import * as ActionTypes from '../actions/actionTypes';
import Style from '../assets/style.scss';
import { StepAction } from '../entities/IStepAction';
import { Tour, TourFactoryFromJS } from '../entities/ITour';
import { ITourStep, TourStep } from '../entities/ITourStep';
import TourUserChoice from '../entities/TourUserChoice';

export default function* () {
	yield take(ActionTypes.INITIALIZE_UI_TOUR_MANAGER);
	yield fork(initializeUITour);
	yield all([
		takeLatest(ActionTypes.TOUR_NEXT_TRANSACTION, handleNext),
		takeLatest(ActionTypes.TOUR_PREV_TRANSACTION, handlePrev),
		takeLatest(ActionTypes.STOP_TOUR_TRANSACTION, handleStop),
		takeLatest(ActionTypes.ON_PATH_OPENED_ACTION, handleOnPathOpened)
	]);
}

function* handleOnPathOpened(action: IBaseAction<any>) {
	const _path: string = action.payload.path;
	const _panelId: string = action.payload.panelId;
	yield call(handleStartTour, _path, _panelId);
}

function* handleStartTour(_path: string, _panelId?: string) {
	const tour: Tour = yield select((state: BaseState) =>
		state.UITour.tours.find(t => t.path === _path)
	);
	const completedTours: string[] = JSON.parse(localStorage.getItem('completedTours') || '[]');
	if (completedTours.findIndex(t => t == _path) == -1 && tour) {
		yield put(loadTourAction(tour.id));
		if (_panelId) {
			yield take(
				(action: any) =>
					action.type == 'APP_MAIN.ANIMATION_END_PANEL' &&
					action.payload.panel.id == _panelId
			);
		}
		document.body.click();
		yield delay(1000);
		yield put(startTourAction());
	}
}

function* handleNext() {
	const nextStep = yield select((state: BaseState) => {
		const tour = state.UITour.tours.get(state.UITour.activeTourId);
		if (tour) {
			const nextStepIndex = tour.currentStep + 1;
			return tour.steps.get(nextStepIndex);
		}
		return null;
	});
	if (nextStep && nextStep.onBeforeActions) {
		yield put(onChangeTourLoadingAction(true));
		yield call(executeStepAction, nextStep);
		yield put(onChangeTourLoadingAction(false));
	}
	yield put(onTourNextAction());
}

function* handlePrev() {
	const prevStep = yield select((state: BaseState) => {
		const tour = state.UITour.tours.get(state.UITour.activeTourId);
		if (tour && tour.currentStep > 0) {
			const prevStepIndex = tour.currentStep - 1;
			return tour.steps.get(prevStepIndex);
		}
		return null;
	});
	if (prevStep && prevStep.onBeforeActions) {
		yield put(onChangeTourLoadingAction(true));
		yield call(executeStepAction, prevStep);
		yield put(onChangeTourLoadingAction(false));
	}
	yield put(onTourPrevAction());
}

function* handleStop() {
	const tour: Tour = yield select((state: BaseState) =>
		state.UITour.tours.get(state.UITour.activeTourId)
	);
	let userChoice = TourUserChoice.ShowLater;
	const modalId = 'uiTourCloseModal';

	if (!tour) {
		return;
	}

	if (tour.currentStep == tour.steps.size - 1) {
		const completedTours: string[] = JSON.parse(localStorage.getItem('completedTours') || '[]');
		completedTours.push(tour.path);
		localStorage.setItem('completedTours', JSON.stringify(completedTours));
		userChoice = TourUserChoice.Completed;
	} else {
		const res: IMessageBoxResolvePayload = yield MessageBox.show({
			id: modalId,
			props: { zIndex: 999999, wrapClassName: Style.tourCloseModalWrap },
			width: '400px',
			title: findMessage.get('101976'),
			content: findMessage.get('101977'),
			buttons: [
				{
					id: 'showLater',
					type: 'primary',
					text: findMessage.get('101978')
				},
				{
					id: 'neverShow',
					text: findMessage.get('101979')
				}
			]
		});
		if (res) {
			if (res.button.id == 'neverShow') {
				userChoice = TourUserChoice.NeverShow;
			} else if (res.button.id == 'showLater') {
				userChoice = TourUserChoice.ShowLater;
			} else {
				//CloseButton
				return;
			}
		}
	}

	yield put(stopTourAction());
	yield put(afterStopTourTransaction());

	const request = {
		secretKey: tour.secretKey,
		userChoice
	};

	try {
		const response = yield ServiceList.login.UiTour.SetUserUITourChoice.call(request);
		if (response) {
			BCNotification.info({ message: findMessage.get('101980') });
		}
	} catch (error) {
		BCNotification.error({ message: findMessage.get('101981') });
		console.log('function*handleStop -> error', error);
	}
}

function* initializeUITour() {
	try {
		const isRedirectOrMobile = window.location.hash.includes('_redirect');
		if (isRedirectOrMobile) {
			return;
		}
		const response = yield ServiceList.login.UiTour.GetAllTours.call();
		if (response) {
			const tours = yield call(parseTourJS, response);
			yield put(setToursAction(tours));

			const activePanelType = yield select((state: BaseState) => {
				if (state.has('layouts')) {
					return (state as any).layouts.leftMenu.activePanelType;
				} else {
					return null;
				}
			});
			/**
			 * Eğer activePanel varsa ve bu panel startupLayout ise redirect'ten gelip gelmediğine bakılıyor.
			 * StartupLayout ise: redirect varsa tour başlatılmayacak, redirect yoksa başlatılacak.
			 * StartupLayout değil ise: tour başlatılacak.
			 * ActivePanel null ise tour başlatılmayacak.
			 */
			const shouldStartTour = activePanelType
				? activePanelType == panelType.startupLayout
					? !isRedirectOrMobile
					: true
				: false;
			if (shouldStartTour) {
				const path = panelType[activePanelType].toString();
				yield call(handleStartTour, path);
			}
		}
	} catch (error) {
		console.log('initializeUITour -> error', error);
	}
}

function* parseTourJS(toursJS: any[]) {
	let parsedTours: Map<string, Tour> = Map();
	try {
		for (const _tour of toursJS) {
			//let _mlTourTitle = _tour.tourTitle && typeof _tour.tourTitle === "string" ? _tour.tourTitle : yield call(getMLProperty, _tour.tourTitle);
			const tourObj = JSON.parse(_tour.steps.replace(/\\/g, ''));
			const _steps: ITourStep[] = [];
			for (const _step of tourObj.steps) {
				const _mlStepTitle =
					_step.title && typeof _step.title === 'string'
						? _step.title
						: yield call(getMLProperty, _step.title);
				const _mlStepContent =
					typeof _step.content === 'string'
						? _step.content
						: yield call(getMLProperty, _step.content);
				const edittedSelector = removeConsecutiveQuotes(_step?.selector);

				_steps.push({
					selector: edittedSelector,
					title: _mlStepTitle,
					content: <div dangerouslySetInnerHTML={{ __html: _mlStepContent }} />,
					stepInteraction: _step.stepInteraction,
					onBeforeActions: _step.onBeforeActions
				});
			}
			parsedTours = parsedTours.set(
				tourObj.id,
				TourFactoryFromJS({
					id: tourObj.id,
					path: tourObj.path,
					currentStep: 0,
					isOpen: false,
					secretKey: _tour.secretKey,
					showTourTitle: true,
					steps: _steps
				})
			);
		}
	} catch (error) {
		console.log('parseTourJS -> error', error);
	}
	return parsedTours;
}

function* executeStepAction(step: TourStep) {
	for (const action of step.onBeforeActions) {
		if (action.type == 'CLICK') {
			yield call(clickStepAction, action);
		} else if (action.type == 'DELAY') {
			yield call(delayStepAction, action);
		}
	}
}

function* delayStepAction(stepAction: StepAction) {
	if (stepAction.data && typeof stepAction.data === 'number') {
		yield delay(stepAction.data);
	}
}

function clickStepAction(stepAction: StepAction) {
	const edittedSelector = removeConsecutiveQuotes(stepAction?.data);
	const el = document.querySelector(edittedSelector as string);

	if (el) {
		(el as any).click();
	}
}

function removeConsecutiveQuotes(data: string | number) {
	if (typeof data === 'string') {
		return data.replace(/'+'/g, "'");
	}
	return String(data);
}
